import { BadRequest, InternalError, NetworkError } from 'common/errors'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { optInFail } from 'publisher/actions/optInActions'
import {
  buyMainOffer,
  CreateOrderResponse,
  CreateSubscriptionResponse,
  captureOrder,
  continuePurchase,
  validateSubscription,
} from 'publisher/api/paypalRestApi'
import { pageSelectors } from 'publisher/store'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { PaymentButtonEntityInterface } from 'src/shared/entities/payment-button/payment-button.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import usePage from '../usePage'
import usePaymentSubmit from './usePaymentSubmit'

export const usePaypalRestPaymentHandler = (
  entity:
    | OldEntityInterface
    | PaymentButtonEntityInterface
    | BookingPaidCalendarInterface,
) => {
  const { t } = useTypedTranslation()
  const { errors, setErrors, isLoading, submit } = usePaymentSubmit(entity)
  const [orderHash, setOrderHash] = useState('')
  const pageId = usePage(pageSelectors.getPageId)
  const dispatch = useDispatch()

  const handleSubmit = async () => {
    return await submit(async body => {
      try {
        const {
          data: { redirect },
        } = await buyMainOffer(pageId, {
          payment_form: body,
        })
        const res = await continuePurchase<
          CreateOrderResponse | CreateSubscriptionResponse
        >(redirect)
        if (res) {
          if (res.data.isBump && res.data.options.paypalLink) {
            window.location.assign(res.data.options.paypalLink)
            return
          }

          setOrderHash(res.data.options.hash)
          if ('paypalOrderId' in res.data.options) {
            return res.data.options.paypalOrderId
          }
          return res.data.options.paypalSubscriptionId
        }
      } catch (error) {
        if (error instanceof BadRequest) {
          setErrors(error.response.data.errors.common)
          dispatch(optInFail({ fields: error.response.data.errors.fields }))
        } else if (error instanceof NetworkError) {
          setErrors([t('core.errors.no_connection')])
        } else if (error instanceof InternalError) {
          setErrors([t('core.error.title')])
        }
      }
    })
  }

  return {
    errors,
    isLoading,
    handleSubmit,
    orderHash,
  }
}

export interface PayPalApprovedData {
  billingToken?: string | null
  facilitatorAccessToken?: string
  orderID: string
  payerID?: string | null
  paymentID?: string | null
  subscriptionID?: string | null
  authCode?: string | null
}

interface BumpPurchaseResponse {
  options: {
    paypalLink: string
  }
}

export const handleCaptureOrder = async (
  body: PayPalApprovedData,
  hash: string,
  isBumpChecked?: boolean,
) => {
  const { data } = await captureOrder({
    paypalOrderId: body.orderID,
    paypalPaymentId: body.paymentID,
    facilitatorAccessToken: body.facilitatorAccessToken,
    paypalPayerId: body.payerID,
    paymentSource: 'paypal',
    hash,
  })

  const redirect = data.redirect || data.redirectLink

  if (isBumpChecked) {
    const res = await continuePurchase<BumpPurchaseResponse>(redirect)
    if (res) {
      window.location.assign(res.data.options.paypalLink)
      return
    }
  }

  if (redirect) {
    window.location.assign(redirect)
  }
}

export const handleValidateSubscription = async (
  body: PayPalApprovedData,
  hash: string,
  isBumpChecked?: boolean,
) => {
  if (body.subscriptionID) {
    const { data } = await validateSubscription({
      paypalSubscriptionId: body.subscriptionID,
      hash,
    })

    const redirect = data.redirect || data.redirectLink

    if (isBumpChecked) {
      const res = await continuePurchase<BumpPurchaseResponse>(redirect)
      if (res) {
        window.location.assign(res.data.options.paypalLink)
        return
      }
    }

    if (redirect) {
      window.location.assign(redirect)
    }
  }
}
